import { petRecipeOperations } from '@exante/gyro-core/pets/catalog';
export const PET_STATUS = {
    off: 'Off', waiting: 'Waiting', working: 'Working', 'needs-review': 'Needs review', paused: 'Paused',
    'needs-connection': 'Needs connection', 'waiting-for-device': 'Waiting for device', 'attention-needed': 'Attention needed',
};
export function petFeatureReason(catalog, feature) {
    if (!catalog.capabilities.enabled)
        return 'AI PETS is not enabled for your organization yet.';
    if (!catalog.capabilities.storageReady)
        return 'Reliable companion storage is unavailable. No new work can be started.';
    if (catalog.capabilities.features?.[feature] === true)
        return undefined;
    return ({ create: 'Creating new companions and guided job suggestions are not enabled for this service.', activate: 'New companion activations and resumes are disabled. You can still save and test a setup. Already activated work remains subject to its existing execution controls.', run: 'Companion execution is disabled. You can inspect existing work and pause a companion; new work cannot start.', replyDraft: 'Automatic Outlook reply drafts are not enabled for this service.', library: 'The company companion library and sharing are not enabled for this service.' })[feature];
}
export function petError(error) { return error instanceof Error ? error.message : 'The request did not complete. Please try again.'; }
export function operationProblems(catalog, template, sources = [], previewOnly = false, relatedContextKinds = []) {
    if (!catalog.capabilities.enabled)
        return ['AI PETS is not enabled for your organization yet.'];
    if (!catalog.capabilities.storageReady)
        return ['Reliable background storage is not ready. Your companion cannot be activated yet.'];
    if (template.id === 'teams-action-companion' || template.id === 'deadline-watch') {
        const problems = [], reason = petFeatureReason(catalog, 'run');
        if (reason)
            problems.push(reason);
        if (catalog.capabilities.monitorPreview?.available !== true)
            problems.push(catalog.capabilities.monitorPreview?.reason || 'Source-backed monitoring preview is not connected for this account.');
        if (!previewOnly && catalog.capabilities.monitorPreview?.backgroundAvailable !== true)
            problems.push('Background monitoring is not connected for this account.');
        if (!sources.length)
            problems.push('Select at least one authorized source to test.');
        if (sources.some(source => template.id === 'teams-action-companion' ? source.kind !== 'teams' : source.kind !== 'mail' && source.kind !== 'files'))
            problems.push(template.id === 'teams-action-companion' ? 'Choose authorized Teams chats for this companion.' : 'Choose authorized mail sources, meeting-note files, or both.');
        return problems;
    }
    if (previewOnly && template.id === 'daily-brief' && catalog.capabilities.dailyPreview) {
        const reason = petFeatureReason(catalog, 'run');
        return reason ? [reason] : catalog.capabilities.dailyPreview.available ? [] : [catalog.capabilities.dailyPreview.reason || 'Source preview is unavailable.'];
    }
    if (template.availability?.available === false)
        return [template.availability.reason || 'This job is not available to run yet.'];
    if (template.id === 'daily-brief' && catalog.capabilities.dailyPreview && template.availability?.available === true) {
        const reason = petFeatureReason(catalog, 'run');
        return reason ? [reason] : [];
    }
    const draftReason = !previewOnly && template.output === 'reply-draft' ? petFeatureReason(catalog, 'replyDraft') : undefined;
    if (draftReason)
        return [draftReason];
    const local = sources.some(source => source.kind === 'device');
    if (local && sources.some(source => source.kind !== 'device'))
        return ['Use either local device sources or cloud sources in this job. Mixed-source execution is not available yet.'];
    const operations = petRecipeOperations(template.id, relatedContextKinds).filter(id => !previewOnly || id !== 'mail.reply-draft').map(id => local && id === 'files.read' ? 'device.files.read' : id);
    return operations.flatMap(id => {
        const operation = catalog.capabilities.operations.find(item => item.id === id);
        return operation?.available ? [] : [operation?.reason || `${id} is not available for this account.`];
    });
}
export function newPetInput(template) {
    let timeZone = 'UTC';
    try {
        timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    }
    catch { /* UTC remains explicit in setup. */ }
    return { name: template.defaultName, avatarId: template.defaultAvatarId, purpose: template.description, definition: {
            ...(template.id === 'meeting-notes-actions' ? { proposedActionDestination: { version: 1, kind: 'pet-review' } } : {}),
            templateId: template.id, instructions: template.instructions, personality: 'Clear, thoughtful and concise.',
            trigger: { kind: template.allowedTriggers.includes('manual') ? 'manual' : template.allowedTriggers[0], timeZone, hour: 9, minute: 0, weekdays: [1, 2, 3, 4, 5], catchUp: 'latest' },
            sources: [], filters: { senders: [], topics: [], exclude: [], attachments: false },
            limits: { maxRunsPerDay: 10, maxTokensPerRun: 4000, maxCostMicrosPerRun: 500000, maxCostMicrosPerDay: 2000000, maxMinutesPerRun: 5 },
        } };
}
export function definitionInput(pet) {
    const { version: _version, hash: _hash, createdAt: _createdAt, ...input } = pet.definition;
    return input;
}
/** Render external evidence links only with an explicit safe web scheme. */
export function safePetLink(value) {
    try {
        const url = new URL(value);
        return url.protocol === 'https:' && !url.username && !url.password ? url.href : undefined;
    }
    catch {
        return undefined;
    }
}
export function previewStillCurrent(preview, pet) {
    return Boolean(preview?.activationEligible && preview.definitionHash === pet.definition.hash && Date.parse(preview.expiresAt) > Date.now());
}
export function listLines(value) { return value.split(/\r?\n|,/).map(item => item.trim()).filter(Boolean); }
/** Defaults apply only to the person's explicit trigger-selection gesture. */
export function selectPetTrigger(definition, kind) {
    const { minutesBefore, intervalMinutes, ...prior } = definition.trigger;
    if (kind === 'interval')
        return {
            trigger: { ...prior, kind, intervalMinutes: intervalMinutes ?? 180, weekdays: [0, 1, 2, 3, 4, 5, 6], catchUp: 'latest' },
            filters: { ...definition.filters, lookbackHours: definition.filters.lookbackHours ?? 24 },
        };
    return { trigger: { ...prior, kind, ...(kind === 'meeting' ? { minutesBefore: minutesBefore ?? 30 } : {}) } };
}
export function draftProblems(input) {
    const problems = [];
    if (!input.name.trim() || input.name.length > 60)
        problems.push('Give your companion a name of 1–60 characters.');
    if (!input.purpose.trim() || input.purpose.length > 200)
        problems.push('Use a short purpose of 1–200 characters.');
    if (!input.definition.instructions.trim() || input.definition.instructions.length > 4000)
        problems.push('Describe the job in 1–4,000 characters.');
    if (input.definition.personality.length > 1000)
        problems.push('Keep the personality within 1,000 characters.');
    const { limits, trigger, filters } = input.definition;
    if (input.definition.sources.length > 20)
        problems.push('Select at most 20 sources for this job.');
    if (input.definition.sources.some(source => source.kind === 'device') && input.definition.sources.length !== 1)
        problems.push('A local-file job uses exactly one protected folder. Remove the other sources before testing.');
    const bounded = (value, min, max) => Number.isInteger(value) && value >= min && value <= max;
    if (!bounded(limits.maxRunsPerDay, 1, 100))
        problems.push('Choose 1–100 runs per day.');
    if (!bounded(limits.maxTokensPerRun, 100, 16000))
        problems.push('Choose 100–16,000 tokens per run.');
    if (!bounded(limits.maxCostMicrosPerRun, 1, 1000000) || !bounded(limits.maxCostMicrosPerDay, 1, 10000000) || limits.maxCostMicrosPerDay < limits.maxCostMicrosPerRun)
        problems.push('Set a positive per-run budget up to $1 and a daily budget up to $10 that covers at least one run.');
    if (!bounded(limits.maxMinutesPerRun, 1, 15))
        problems.push('Choose 1–15 minutes per run.');
    if (!bounded(trigger.hour, 0, 23) || !bounded(trigger.minute, 0, 59) || !trigger.weekdays.length)
        problems.push('Choose a valid schedule time and at least one day.');
    if (trigger.kind === 'meeting' && (input.definition.templateId !== 'meeting-prep' || !bounded(trigger.minutesBefore ?? 0, 5, 1440)))
        problems.push('Meeting Prep needs a lead time of 5–1,440 minutes.');
    if (trigger.kind === 'interval') {
        if (input.definition.kind === 'mission-actions' || !['teams-action-companion', 'deadline-watch'].includes(input.definition.templateId ?? ''))
            problems.push('Monitoring intervals are available only for Teams Action Companion and Deadline Watch.');
        if (!bounded(trigger.intervalMinutes ?? 0, 5, 1440))
            problems.push('Choose an interval of 5–1,440 whole minutes.');
        if (trigger.catchUp !== 'latest' || trigger.weekdays.length !== 7 || new Set(trigger.weekdays).size !== 7 || trigger.weekdays.some(day => !bounded(day, 0, 6)))
            problems.push('Monitoring intervals use every day and the latest missed opportunity.');
        if (!bounded(filters.lookbackHours ?? 0, 1, 8760))
            problems.push('Choose a lookback of 1–8,760 whole hours before reviewing recurring monitoring.');
    }
    else if (trigger.intervalMinutes !== undefined)
        problems.push('Interval minutes apply only to monitoring intervals.');
    try {
        new Intl.DateTimeFormat('en-US', { timeZone: trigger.timeZone });
    }
    catch {
        problems.push('Enter a valid time zone, such as America/Los_Angeles.');
    }
    if (filters.senders.length > 50 || filters.senders.some(item => item.length > 200))
        problems.push('Use at most 50 sender rules, up to 200 characters each.');
    if ([filters.topics, filters.exclude].some(items => items.length > 30 || items.some(item => item.length > 150)))
        problems.push('Use at most 30 topic or exclusion rules, up to 150 characters each.');
    if (filters.lookbackHours !== undefined && !bounded(filters.lookbackHours, 1, 8760) || filters.waitingHours !== undefined && !bounded(filters.waitingHours, 1, 8760))
        problems.push('Lookback and waiting windows must be 1–8,760 whole hours.');
    if (filters.domains && (filters.domains.length > 20 || filters.domains.some(value => !/^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/.test(value))))
        problems.push('Use at most 20 lowercase website domains, without URLs, paths or wildcards.');
    if (filters.fileExtensions && (filters.fileExtensions.length > 20 || filters.fileExtensions.some(value => !/^[a-z0-9]{1,12}$/.test(value))))
        problems.push('Use at most 20 lowercase file extensions without dots, up to 12 letters or digits.');
    if (filters.checklist && (filters.checklist.length > 30 || filters.checklist.some(value => !value.trim() || value.length > 500)))
        problems.push('Use at most 30 checklist items, 1–500 characters each.');
    if (filters.workHours && (!bounded(filters.workHours.startHour, 0, 23) || !bounded(filters.workHours.endHour, 1, 24) || filters.workHours.startHour >= filters.workHours.endHour))
        problems.push('Working hours must end after they start, within the same day.');
    if ([filters.pinnedProjects, filters.excludedProjects].some(items => items && (items.length > 30 || items.some(item => !item.trim() || item.length > 40))))
        problems.push('Use at most 30 pinned or excluded project codes, 1–40 characters each.');
    return problems;
}
//# sourceMappingURL=model.js.map